/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  addComplaint,
  fetchAccusedProsecutor,
  fetchZoneByAdminUnit,
} from "services/prosecutorDecipilineService";
import { AccusedProsecutor, AdminUnit, Complaints } from "types";

import ConfirmationModal from "components/ConfirmationModal";

import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { COMPLIANT_STATUS, OFFENSE_TYPE } from "utils/constantValues";
import { fetchAdminUnitDetail } from "services/advocateComplaintService";

const formatDate = (date: any) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const subtractYears = (date: any, years: any) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() - years);
  return d;
};

const NewComplaint: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorQuery, setErrorQuery] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const contextData = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [formState, setFormState] = useState<Complaints>({
    uu_id: 0,
    receiving_prosecutor: null,
    assigned_prosecutor: "",
    reporters: [],
    victims: [],
    witnesses: [],
    accused_prosecutor_name: "",
    accused_prosecutor_phone: "",
    reception_date: null,
    accused_prosecutor_job_status: "",
    offence_type: "",
    accused_prosecutor_gender: "",
    offence_description: "",
    decision_type: null,
    decision_summary: "",
    assigned_date: null,
    date_of_offense: null,
    prosecutor_offence_region: "oromia",
    prosecutor_offence_zone: "",
    prosecutor_offence_woreda: "",
    status: COMPLIANT_STATUS?.PENDING,
  });
  const [wereda, setWereda] = useState<string>("");
  const [zone, setZone] = useState<string>("");

  const [accusedProsecutorsData, setAccusedProsecutorsData] = useState<
    AccusedProsecutor[]
  >([]);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [offenseZone, setOffenseZone] = useState<AdminUnit[]>([]);
  const [offenseWoreda, setOffenseWoreda] = useState<AdminUnit[]>([]);
  const [hirarchy, setHirarchi] = useState<any>();
  const [isAccusationReportByMobile, setIsAccusationReportByMobile] =
    useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!query && !zone) return;

    const loadAccusedProsecutors = async () => {
      setLoading(true);
      try {
        const data = await fetchAccusedProsecutor(
          query,
          contextData.user.admin_unit,
          wereda,
          zone,
        );

        console.log("Search results:", data);
        console.log(
          "Filtered results:",
          data?.filter((a) => a?.admin_unit === user?.admin_unit),
        );

        setAccusedProsecutorsData(
          data?.filter((a) => a?.admin_unit === user?.admin_unit) as any,
        );
        setErrorQuery("");

        // Show dropdown if we have results and query is not empty
        if (query.trim() && data?.length > 0) {
          setShowDropdown(true);
        }
      } catch (err) {
        setErrorQuery(query ? "Failed to load prosecutors." : "");
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to prevent too many API calls
    const timer = setTimeout(() => {
      loadAccusedProsecutors();
    }, 300);

    return () => clearTimeout(timer);
  }, [query, wereda, zone]);

  useEffect(() => {
    const loadProsecutorOffenceZone = async () => {
      setLoading(true);
      try {
        if (!contextData.user.admin_unit) return;
        let data: any = await fetchZoneByAdminUnit(
          contextData.user.admin_unit,
          contextData.user.org_unit,
        );
        data = await fetchZoneByAdminUnit(
          contextData.user.admin_unit || user?.admin_unit,
          contextData.user.org_unit,
        );
        setHirarchi(data);
        if (data.admin_unit.hierarchy_level.name === "Zonal") {
          setZone(contextData.user.admin_unit || user?.admin_unit);
        }
        setOffenseZone(data.child_admin_units.units);
      } catch (err) {
        // Error handling
      } finally {
        setLoading(false);
      }
    };
    if (contextData.user.admin_unit) loadProsecutorOffenceZone();
  }, [contextData.user.admin_unit]);

  useEffect(() => {
    const loadProsecutorOffenceWoreda = async () => {
      setLoading(true);
      try {
        const data: any = await fetchZoneByAdminUnit(
          offenseZone.find(
            (z) => z.unit_name === formState.prosecutor_offence_zone,
          )?.uu_id || "",
          contextData.user.org_unit,
        );
        setOffenseWoreda(data.child_admin_units.units);
      } catch (err) {
        // Error handling
      } finally {
        setLoading(false);
      }
    };
    loadProsecutorOffenceWoreda();
  }, [formState.prosecutor_offence_zone]);

  useEffect(() => {
    const loadProsecutorOffenceWoreda = async () => {
      setLoading(true);
      try {
        const data: any = await fetchZoneByAdminUnit(
          offenseZone.find((z) => z.uu_id === zone)?.uu_id || "",
          contextData.user.org_unit,
        );
        setOffenseWoreda(data.child_admin_units.units);
      } catch (err) {
        // Error handling
      } finally {
        setLoading(false);
      }
    };
    loadProsecutorOffenceWoreda();
  }, [zone]);

  useEffect(() => {
    setLoading(true);
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAccusedProsecutorSelect = (
    accused: AccusedProsecutor,
    name: string,
  ) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: accused,
      accused_prosecutor_gender:
        accused?.gender?.toLowerCase() ?? prevState.accused_prosecutor_gender,
      accused_prosecutor_job_status: "Active",
      accused_prosecutor_phone: accused?.mobile_phone_number ?? "",
    }));
    setQuery("");
    setShowDropdown(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (value.trim()) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setAccusedProsecutorsData([]);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: boolean } = {};

    if (!formState.reception_date) {
      errors.reception_date = true;
      setInvalidFields(errors);
      return "Reception Date is Required.";
    }

    if (!formState.date_of_offense) {
      errors.date_of_offense = true;
      setInvalidFields(errors);
      return "Date of Offense is Required.";
    }

    const offenseDate = new Date(formState.date_of_offense);
    const receptionDate = new Date(formState.reception_date);
    const yearDiff =
      (receptionDate.getTime() - offenseDate.getTime()) /
      (1000 * 3600 * 24 * 365.25);

    const offenseType = formState.offence_type?.toLowerCase();

    if (offenseType.includes(OFFENSE_TYPE.GRAVE_OFFENSE) && yearDiff > 4) {
      errors.reception_date = true;
      errors.date_of_offense = true;
      setInvalidFields(errors);
      return t("graveOffenseError");
    }

    if (offenseType.includes(OFFENSE_TYPE.MEDIUM_OFFENSE) && yearDiff > 3) {
      errors.reception_date = true;
      errors.date_of_offense = true;
      setInvalidFields(errors);
      return t("mediumOffenseError");
    }

    if (offenseType.includes(OFFENSE_TYPE.PITY_OFFENSE) && yearDiff > 2) {
      errors.reception_date = true;
      errors.date_of_offense = true;
      setInvalidFields(errors);
      return t("pityOffenseError");
    }

    setInvalidFields({});
    return "";
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = validateForm();

    if (error) {
      showToast(error, "error");
      return;
    }

    if (!showConfirmationModal) {
      setShowConfirmationModal(true);
      return;
    }
  };

  const handleConfirm = async () => {
    setSubmitLoading(true);

    try {
      const resData: any = (await addComplaint({
        ...formState,
        cordinator_id: user?.user_id,
        user_id_receiving_prosecutor: user?.user_id,
        receiving_prosecutor: user?.full_name,
        admin_unit_id: user?.admin_unit,
        org_id: user?.org_unit,
        accused_prosecutor_org_id:
          typeof formState?.accused_prosecutor === "object" &&
          formState?.accused_prosecutor !== null
            ? formState.accused_prosecutor.org_unit
            : "",
        accused_prosecutor_admin_unit_id:
          typeof formState?.accused_prosecutor === "object" &&
          formState?.accused_prosecutor !== null
            ? formState.accused_prosecutor.admin_unit
            : "",
        accused_prosecutor_name:
          typeof formState?.accused_prosecutor === "object" &&
          formState?.accused_prosecutor !== null
            ? (formState.accused_prosecutor.full_name ?? "")
            : ((formState?.accused_prosecutor as string) ?? ""),
        user_id_accused_prosecutor_name:
          typeof formState?.accused_prosecutor === "object" &&
          formState?.accused_prosecutor !== null
            ? formState.accused_prosecutor.uu_id
            : (formState?.accused_prosecutor as string),
        is_regional:
          adminUnitData?.hierarchy_level?.name === "Regional" ? true : false,
      })) as Complaints;

      navigate(`/complaint-request/${resData?.complaint?.uu_id}`);
      showToast(t("successfullyCreated"), "success");
      setShowConfirmationModal(false);
      setFormState({
        uu_id: 0,
        receiving_prosecutor: null,
        assigned_prosecutor: "",
        reporters: [],
        victims: [],
        witnesses: [],
        accused_prosecutor_name: "",
        accused_prosecutor_phone: "",
        reception_date: null,
        offence_type: "",
        offence_description: "",
        decision_type: null,
        decision_summary: "",
        assigned_date: null,
        status: COMPLIANT_STATUS?.PENDING,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Official Header */}
        <div className="mb-6 border-b-2 border-dashed border-gray-300 pb-8">
          <div className="flex items-start gap-6 mb-6">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-amber-600 to-amber-700 flex items-center justify-center shadow-lg">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-800 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">✏️</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-0.5 bg-red-800"></div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                New Dicipline Complaint Registration
              </h1>
              <p className="text-gray-600 max-w-2xl">
                Official form for registering disciplinary complaints against
                prosecutors. All fields marked with{" "}
                <span className="text-red-800 font-bold">*</span> are mandatory.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Prosecutor Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300">
            {/* Section Header with Golden Accent */}
            <div className="border-b border-gray-300">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-8 bg-gradient-to-b from-red-800 to-red-900 rounded-r"></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      <span className="text-red-800">I.</span> Prosecutor
                      Information
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Select accused prosecutor and provide work location
                      details
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Work Location Section */}
                <div className="lg:col-span-1 space-y-6">
                  <div className="border border-gray-400 p-5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-300">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-gray-700 to-gray-800 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">
                          Work Location Details
                        </h3>
                        <p className="text-sm text-gray-600">
                          Administrative jurisdiction information
                        </p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {hirarchy?.admin_unit.hierarchy_level.name !==
                        "Zonal" && (
                        <div>
                          <label className="block mb-3">
                            <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                              Zone
                              <span className="text-red-800 font-bold ml-1">
                                *
                              </span>
                            </span>
                            <span className="text-xs text-gray-500 block mb-2">
                              Select administrative zone
                            </span>
                          </label>
                          <select
                            className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all appearance-none"
                            value={zone}
                            onChange={(e) => {
                              setZone(e.target.value);
                              // Clear search when zone changes
                              setQuery("");
                              setAccusedProsecutorsData([]);
                              setShowDropdown(false);
                            }}
                          >
                            <option value="" className="text-gray-400">
                              -- Select Zone --
                            </option>
                            {(offenseZone || []).map((z) => (
                              <option
                                value={z.uu_id}
                                key={z.uu_id}
                                className="text-gray-800"
                              >
                                {z.unit_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {hirarchy?.admin_unit.hierarchy_level.name === "Zonal" &&
                        zone && (
                          <div>
                            <label className="block mb-3">
                              <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                                Woreda
                                <span className="text-red-800 font-bold ml-1">
                                  *
                                </span>
                              </span>
                              <span className="text-xs text-gray-500 block mb-2">
                                Select administrative woreda
                              </span>
                            </label>
                            <select
                              className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                              value={wereda}
                              onChange={(e) => {
                                setWereda(e.target.value);
                                // Clear search when wereda changes
                                setQuery("");
                                setAccusedProsecutorsData([]);
                                setShowDropdown(false);
                              }}
                            >
                              <option value="" className="text-gray-400">
                                -- Select Woreda --
                              </option>
                              {offenseZone?.map((z) => (
                                <option
                                  value={z.uu_id}
                                  key={z.uu_id}
                                  className="text-gray-800"
                                >
                                  {z.unit_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      {hirarchy?.admin_unit.hierarchy_level.name !== "Zonal" &&
                        zone && (
                          <div>
                            <label className="block mb-3">
                              <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                                Woreda
                                <span className="text-red-800 font-bold ml-1">
                                  *
                                </span>
                              </span>
                              <span className="text-xs text-gray-500 block mb-2">
                                Select administrative woreda
                              </span>
                            </label>
                            <select
                              className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                              value={wereda}
                              onChange={(e) => {
                                setWereda(e.target.value);
                                // Clear search when wereda changes
                                setQuery("");
                                setAccusedProsecutorsData([]);
                                setShowDropdown(false);
                              }}
                            >
                              <option value="" className="text-gray-400">
                                -- Select Woreda --
                              </option>
                              {offenseWoreda?.map((z) => (
                                <option
                                  value={z.uu_id}
                                  key={z.uu_id}
                                  className="text-gray-800"
                                >
                                  {z.unit_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      {/* New checkbox for mobile report */}
                      <div className="mb-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAccusationReportByMobile || false}
                            onChange={(e) =>
                              setIsAccusationReportByMobile(
                                !isAccusationReportByMobile,
                              )
                            }
                            className="w-5 h-5 text-red-800 border-2 border-gray-400 rounded focus:ring-red-800 focus:ring-2 transition"
                          />
                          <span className="text-sm font-semibold text-gray-700">
                            Is Accusation report By mobile?
                          </span>
                        </label>
                      </div>

                      {/* Conditional Report Application Number */}
                      {isAccusationReportByMobile && (
                        <div>
                          <label className="block mb-3">
                            <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                              Report Application Number
                            </span>
                          </label>
                          <input
                            type="text"
                            value={formState?.report_application_number || ""}
                            onChange={(e) =>
                              setFormState((prev) => ({
                                ...prev,
                                report_application_number: e.target.value,
                              }))
                            }
                            placeholder="e.g., APP-2024-00123"
                            className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Accused Prosecutor Section (unchanged) */}
                <div className="lg:col-span-2 space-y-6" ref={dropdownRef}>
                  <div className="border border-gray-400 p-5 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="flex items-center gap-3 mb-5 pb-3 border-b border-gray-300">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-800 to-red-900 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">
                          Accused Prosecutor
                        </h3>
                        <p className="text-sm text-gray-600">
                          Identify the prosecutor against whom the complaint is
                          filed
                        </p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {/* Search Section */}
                      <div className="relative">
                        <label className="block mb-3">
                          <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                            Search Prosecutor
                            <span className="text-red-800 font-bold ml-1">
                              *
                            </span>
                          </span>
                          <span className="text-xs text-gray-500 block mb-2">
                            Enter name or working ID
                          </span>
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={query}
                            onChange={handleSearchChange}
                            onFocus={() => {
                              if (
                                query.trim() &&
                                accusedProsecutorsData.length > 0
                              ) {
                                setShowDropdown(true);
                              }
                            }}
                            placeholder="Enter prosecutor name or working ID..."
                            className="w-full pl-12 pr-10 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                          />
                          {loading && (
                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                              <div className="w-5 h-5 border-2 border-gray-400 border-t-red-800 rounded-full animate-spin"></div>
                            </div>
                          )}
                          {!loading && query && (
                            <button
                              type="button"
                              onClick={() => {
                                setQuery("");
                                setAccusedProsecutorsData([]);
                                setShowDropdown(false);
                                searchInputRef.current?.focus();
                              }}
                              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-800"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>

                        {/* Loading State */}
                        {loading && (
                          <div className="mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-sm text-red-800">
                                Searching official registry...
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Error State */}
                        {errorQuery && (
                          <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <svg
                                className="w-5 h-5 text-red-800 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span className="text-sm text-red-800">
                                {errorQuery}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Dropdown Results */}
                        {showDropdown &&
                          query.trim() &&
                          accusedProsecutorsData.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-400 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                              {accusedProsecutorsData?.map((p: any) => (
                                <div
                                  key={p.uu_id}
                                  className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-300 last:border-b-0 transition-colors"
                                  onClick={() => {
                                    handleAccusedProsecutorSelect(
                                      p,
                                      "accused_prosecutor",
                                    );
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-700 to-gray-800 flex items-center justify-center text-white text-xs font-bold">
                                        {p.english_first_name?.[0] || "P"}
                                      </div>
                                      <div>
                                        <div className="font-semibold text-gray-900">
                                          {p.full_name}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-2">
                                          <span className="bg-gray-200 px-2 py-0.5 rounded">
                                            ID: {p?.working_id || "N/A"}
                                          </span>
                                          <span>
                                            {p.employee_type?.name ||
                                              "Prosecutor"}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <svg
                                      className="w-5 h-5 text-red-800"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                        {/* No Results Message */}
                        {showDropdown &&
                          query.trim() &&
                          !loading &&
                          accusedProsecutorsData.length === 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-400 rounded-lg shadow-xl p-4">
                              <div className="flex items-center gap-3 text-gray-600">
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span>
                                  No prosecutors found matching "{query}"
                                </span>
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Selected Prosecutor Card */}
                      {formState?.accused_prosecutor && (
                        <div className="bg-gradient-to-r from-gray-50 to-white border-2 border-red-200 rounded-lg p-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-gray-700 to-gray-800 flex items-center justify-center text-white font-bold text-lg">
                                  {typeof formState?.accused_prosecutor ===
                                    "object" &&
                                  formState?.accused_prosecutor !== null
                                    ? formState.accused_prosecutor
                                        .english_first_name?.[0] || "P"
                                    : "P"}
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-800 rounded-full border-2 border-white flex items-center justify-center">
                                  <span className="text-xs text-white">✓</span>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 text-lg">
                                  {typeof formState?.accused_prosecutor ===
                                    "object" &&
                                  formState?.accused_prosecutor !== null
                                    ? `${formState.accused_prosecutor.english_first_name || "-"} 
                             ${formState.accused_prosecutor.english_middle_name || ""} 
                             ${formState.accused_prosecutor.english_last_name || ""}`
                                    : "-"}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  {typeof formState?.accused_prosecutor ===
                                    "object" &&
                                  formState?.accused_prosecutor !== null
                                    ? formState.accused_prosecutor.employee_type
                                        ?.name || ""
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setFormState((prev) => ({
                                  ...prev,
                                  accused_prosecutor: null,
                                }));
                                setQuery("");
                                searchInputRef.current?.focus();
                              }}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <svg
                                className="w-5 h-5 text-gray-500 hover:text-red-800"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>

                          {/* Prosecutor Details Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="bg-white p-3 rounded border border-gray-300">
                              <div className="text-xs text-gray-500 mb-1">
                                Phone
                              </div>
                              <div className="font-medium text-gray-800">
                                {formState.accused_prosecutor_phone || "N/A"}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded border border-gray-300">
                              <div className="text-xs text-gray-500 mb-1">
                                Gender
                              </div>
                              <div className="font-medium text-gray-800">
                                {formState.accused_prosecutor_gender || "N/A"}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded border border-gray-300">
                              <div className="text-xs text-gray-500 mb-1">
                                Status
                              </div>
                              <div className="font-medium text-gray-800 flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                {formState.accused_prosecutor_job_status ||
                                  "Active"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Case Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300">
            <div className="border-b border-gray-300">
              <div className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-8 bg-gradient-to-b from-red-800 to-red-900 rounded-r"></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      <span className="text-red-800">II.</span> Case Details
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Provide comprehensive details about the complaint and
                      offense
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        Offense Classification
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                      <span className="text-xs text-gray-500 block mb-2">
                        Select appropriate offense category
                      </span>
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 appearance-none transition-all"
                      name="offence_type"
                      value={formState.offence_type}
                      onChange={handleChange}
                    >
                      <option value="" className="text-gray-400">
                        -- Select Classification --
                      </option>
                      <option value="grave" className="text-gray-800">
                        ⚖️ Grave Disciplinary Offense
                      </option>
                      <option value="medium" className="text-gray-800">
                        ⚖️ Medium Disciplinary Offense
                      </option>
                      <option value="petty" className="text-gray-800">
                        ⚖️ Petty Disciplinary Offense
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        Complaint Reception Date
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                      <span className="text-xs text-gray-500 block mb-2">
                        Official date of complaint receipt
                      </span>
                    </label>
                    <input
                      required
                      className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-gray-800 focus:outline-none focus:ring-1 transition-all ${
                        invalidFields.reception_date
                          ? "border-red-800 focus:border-red-800 focus:ring-red-200"
                          : "border-gray-400 focus:border-red-800 focus:ring-red-800"
                      }`}
                      name="reception_date"
                      type="date"
                      value={formState.reception_date?.toString() ?? ""}
                      onChange={handleChange}
                    />
                    {invalidFields.reception_date && (
                      <p className="text-xs text-red-800 mt-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t("dateRequired")}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block mb-3">
                      <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                        Date of Alleged Offense
                        <span className="text-red-800 font-bold ml-1">*</span>
                      </span>
                      <span className="text-xs text-gray-500 block mb-2">
                        When the offense reportedly occurred
                      </span>
                    </label>
                    <input
                      required
                      className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-gray-800 focus:outline-none focus:ring-1 transition-all ${
                        invalidFields.date_of_offense
                          ? "border-red-800 focus:border-red-800 focus:ring-red-200"
                          : "border-gray-400 focus:border-red-800 focus:ring-red-800"
                      }`}
                      name="date_of_offense"
                      type="date"
                      min={
                        formState?.reception_date
                          ? formatDate(
                              subtractYears(
                                formState.reception_date,
                                formState.offence_type === "grave"
                                  ? 4
                                  : formState.offence_type === "medium"
                                    ? 3
                                    : 2,
                              ),
                            )
                          : ""
                      }
                      max={
                        formState?.reception_date
                          ? formatDate(formState?.reception_date)
                          : ""
                      }
                      value={formState.date_of_offense?.toString() ?? ""}
                      onChange={handleChange}
                    />
                    {invalidFields.date_of_offense && (
                      <p className="text-xs text-red-800 mt-2 flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t("dateRequired")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Offense Location */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">
                  Offense Location
                </h3>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg border border-gray-400">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block mb-3">
                        <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                          Administrative Region
                        </span>
                        <span className="text-xs text-gray-500 block mb-2">
                          Region where offense occurred
                        </span>
                      </label>
                      <select
                        required
                        className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                        name="prosecutor_offence_region"
                        value={formState.prosecutor_offence_region}
                        onChange={handleChange}
                      >
                        <option value="" className="text-gray-400">
                          -- Select Region --
                        </option>
                        <option value="oromia" className="text-gray-800">
                          Oromia Regional State
                        </option>
                      </select>
                    </div>

                    {formState.prosecutor_offence_region && (
                      <div>
                        <label className="block mb-3">
                          <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                            Zone / City Administration
                          </span>
                          <span className="text-xs text-gray-500 block mb-2">
                            Administrative zone
                          </span>
                        </label>
                        <select
                          className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                          name="prosecutor_offence_zone"
                          value={formState.prosecutor_offence_zone}
                          onChange={handleChange}
                        >
                          <option value="" className="text-gray-400">
                            -- Select Zone --
                          </option>
                          {(offenseZone || []).map((z) => (
                            <option
                              value={z.unit_name}
                              key={z.uu_id}
                              className="text-gray-800"
                            >
                              {z.unit_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formState.prosecutor_offence_zone && (
                      <div>
                        <label className="block mb-3">
                          <span className="text-sm font-semibold text-gray-700 mb-2 inline-block">
                            Woreda / Sub-City
                          </span>
                          <span className="text-xs text-gray-500 block mb-2">
                            Lowest administrative unit
                          </span>
                        </label>
                        <select
                          className="w-full px-4 py-3 bg-white border-2 border-gray-400 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 transition-all"
                          name="prosecutor_offence_woreda"
                          value={formState.prosecutor_offence_woreda}
                          onChange={handleChange}
                        >
                          <option value="" className="text-gray-400">
                            -- Select Woreda --
                          </option>
                          {offenseWoreda?.map((z) => (
                            <option
                              value={z.unit_name}
                              key={z.uu_id}
                              className="text-gray-800"
                            >
                              {z.unit_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Offense Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">
                  Detailed Offense Description
                </h3>
                <div className="rounded-lg p-1 bg-gradient-to-b from-white to-gray-50">
                  <div className="bg-white rounded-lg p-5">
                    <div className="relative">
                      <textarea
                        rows={6}
                        name="offence_description"
                        value={formState.offence_description}
                        onChange={handleChange}
                        placeholder="Enter detailed description of the alleged offense. Be specific about dates, locations, individuals involved, and any supporting evidence..."
                        className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:border-red-800 focus:ring-1 focus:ring-red-800 resize-none transition-all font-sans"
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-300">
                        Characters: {formState.offence_description.length}/2000
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-gray-600 flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Ensure accuracy as this description becomes part of
                      official records
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300 p-6">
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-500 transition-all font-semibold flex items-center gap-3"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Return to Dashboard
              </button>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormState({
                      uu_id: 0,
                      receiving_prosecutor: null,
                      assigned_prosecutor: "",
                      reporters: [],
                      victims: [],
                      witnesses: [],
                      accused_prosecutor_name: "",
                      accused_prosecutor_phone: "",
                      reception_date: null,
                      offence_type: "",
                      offence_description: "",
                      decision_type: null,
                      decision_summary: "",
                      assigned_date: null,
                      status: COMPLIANT_STATUS?.PENDING,
                    });
                    setQuery("");
                    setZone("");
                    setWereda("");
                  }}
                  className="px-8 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-500 transition-all font-semibold"
                >
                  Clear All Fields
                </button>
                <button
                  className="px-10 py-3 bg-gradient-to-r from-red-800 to-red-900 text-white rounded-lg hover:from-red-900 hover:to-red-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold shadow-lg hover:shadow-xl flex items-center gap-3 group"
                  type="submit"
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing Submission...
                    </>
                  ) : (
                    <>
                      <div className="relative">
                        <svg
                          className="w-6 h-6 group-hover:scale-110 transition-transform"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                      </div>
                      <span className="text-lg">Submit Official Complaint</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={submitLoading}
      />
    </div>
  );
};

export default NewComplaint;
